<template>
  <div v-redirect-if-no-permission:MANAGE_FINANCE_ACCOUNTS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <!-- Edit rule mapping dialog -->
    <v-dialog v-model="editDialog" max-width="860px" persistent>
      <v-card v-if="editedRule">
        <v-card-title>
          {{ $t("finance.postingRules.editMapping") }}: {{ ruleLabel(editedRule) }}
          <v-spacer></v-spacer>
          <v-chip small :color="editedRule.is_active ? 'success' : 'grey'" text-color="white">
            {{ editedRule.is_active ? $t("finance.postingRules.active") : $t("finance.postingRules.inactive") }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <p class="grey--text">{{ $t("finance.postingRules.editMappingHint") }}</p>
          <p v-if="appliesWhen(editedRule)" class="grey--text">{{ $t("finance.postingRules.appliesWhen") }}: {{ appliesWhen(editedRule) }}</p>
          <v-layout v-for="line in editForm.lines" :key="line.id" wrap align-center>
            <v-flex xs12 md2 class="pa-2">
              <v-chip small :color="line.entry_type === 'debit' ? 'primary' : 'teal'" text-color="white">
                {{ line.entry_type === 'debit' ? $t("finance.postingRules.debit") : $t("finance.postingRules.credit") }}
              </v-chip>
            </v-flex>
            <v-flex xs12 md3 class="pa-2">
              <span>{{ formatAmountKey(line.amount_key) }}</span>
            </v-flex>
            <v-flex xs12 md3 class="pa-2">
              <span class="grey--text">{{ defaultAccountLabel(line) }}</span>
            </v-flex>
            <v-flex xs12 md4 class="pa-2">
              <v-autocomplete
                v-model="line.gl_account_id"
                :items="accountOptions"
                item-text="text"
                item-value="value"
                :label="$t('finance.postingRules.overrideAccount')"
                clearable
              />
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="closeEditDialog">{{ $t("finance.common.back") }}</v-btn>
          <v-btn color="primary" :disabled="!canManagePostingRules" @click="saveRule">{{ $t("finance.postingRules.save") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Manual operation entry dialog -->
    <v-dialog v-model="triggerDialog" max-width="640px" persistent>
      <v-card v-if="triggeredRule">
        <v-card-title>{{ $t("finance.postingRules.manualEntry") }}: {{ ruleLabel(triggeredRule) }}</v-card-title>
        <v-card-text>
          <v-layout wrap>
            <v-flex v-for="key in triggerAmountKeys" :key="key" xs12 md6 class="pa-2">
              <v-text-field
                v-model.number="triggerForm.amounts[key]"
                :label="formatAmountKey(key)"
                type="number"
              />
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field
                v-model="triggerForm.operation_ref"
                :label="$t('finance.postingRules.operationRef')"
              />
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field
                v-model="triggerForm.journal_date"
                :label="$t('finance.common.date')"
                type="date"
              />
            </v-flex>
            <!-- Disposal events must name the car: posting also auto-ends its
                 depreciation schedule (policy doc 2026-08-23 item 11). -->
            <v-flex v-if="triggeredRuleNeedsCar" xs12 md6 class="pa-2">
              <v-text-field
                v-model.number="triggerForm.car_id"
                :label="$t('finance.postingRules.carId')"
                type="number"
              />
            </v-flex>
            <!-- Resolver lines (driver/owner/business/center sub-ledgers,
                 gateway accounts) need the target entity so the backend can
                 resolve the GL account. Offered for every such rule, but only
                 required (triggerContextMissing) for a line the engine will
                 really resolve — not one re-pointed to a fixed account, nor
                 one whose amount is left blank/zero (review 2026-09-01). -->
            <v-flex v-for="field in triggerContextFields" :key="field.key" xs12 md6 class="pa-2">
              <v-text-field
                v-if="field.type === 'number'"
                v-model.number="triggerForm[field.key]"
                :label="$t('finance.postingRules.' + field.label)"
                type="number"
              />
              <v-text-field
                v-else
                v-model="triggerForm[field.key]"
                :label="$t('finance.postingRules.' + field.label)"
              />
            </v-flex>
            <v-flex xs12 class="pa-2">
              <v-text-field v-model="triggerForm.memo" :label="$t('finance.postingRules.memo')" />
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="triggerDialog = false">{{ $t("finance.common.back") }}</v-btn>
          <v-btn
            color="primary"
            :disabled="!triggerForm.operation_ref || !hasAmountToPost || (triggeredRuleNeedsCar && !triggerForm.car_id) || triggerContextMissing"
            @click="submitTrigger"
          >
            {{ $t("finance.postingRules.post") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card>
      <div class="fin-header">
        <div class="fin-toolbar">
          <span class="fin-title">{{ $t("finance.postingRules.pageTitle") }}</span>
          <v-chip small class="ms-2" :color="engineEnabled ? 'success' : 'orange'" text-color="white">
            {{ engineEnabled ? $t("finance.postingRules.engineOn") : $t("finance.postingRules.engineOff") }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="loadAll">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ $t("finance.common.refresh") }}</span>
          </v-tooltip>
        </div>
      </div>

      <v-tabs v-model="tab" color="white" slider-color="primary">
        <v-tab>{{ $t("finance.postingRules.rulesTab") }}</v-tab>
        <v-tab>{{ $t("finance.postingRules.failuresTab") }}</v-tab>
        <v-tab>{{ $t("finance.postingRules.depreciationTab") }}</v-tab>
      </v-tabs>

      <!-- Rules -->
      <v-card-text v-show="tab === 0">
        <v-layout wrap class="mb-2">
          <v-flex xs12 md5 class="pa-1">
            <v-text-field
              v-model="ruleSearch"
              :label="$t('finance.common.search')"
              append-icon="search"
              clearable
              hide-details
            />
          </v-flex>
          <v-flex xs12 md4 class="pa-1">
            <v-select
              v-model="ruleKind"
              :items="ruleKindOptions"
              item-text="text"
              item-value="value"
              :label="$t('finance.postingRules.entryKind')"
              hide-details
            />
          </v-flex>
          <v-flex xs12 md3 class="pa-1 grey--text" style="align-self: center">
            {{ filteredRules.length }} / {{ rules.length }}
          </v-flex>
        </v-layout>

        <v-data-table
          class="fin-dt"
          :headers="ruleHeaders"
          :items="filteredRules"
          hide-actions
          item-key="id"
        >
          <template v-slot:items="props">
            <td>
              <v-chip v-if="props.item.doc_ref" small :color="docRefColor(props.item.doc_ref)" text-color="white">
                {{ props.item.doc_ref }}
              </v-chip>
              <span v-else class="grey--text">-</span>
            </td>
            <td>{{ props.item.event_key }}</td>
            <td>
              {{ ruleLabel(props.item) }}
              <!-- Ride rules: the car model x payment channel the engine
                   picks this rule for (doc 2026-10-03 item 1.6). -->
              <div v-if="appliesWhen(props.item)" class="grey--text caption mt-1">
                {{ $t("finance.postingRules.appliesWhen") }}: {{ appliesWhen(props.item) }}
              </div>
            </td>
            <td>
              <div v-for="line in props.item.lines" :key="line.id">
                <small>
                  {{ line.entry_type === 'debit' ? $t("finance.postingRules.debit") : $t("finance.postingRules.credit") }}
                  ({{ formatAmountKey(line.amount_key) }}) &larr;
                  {{ line.account ? (line.account.code + ' ' + accountName(line.account)) : defaultAccountLabel(line) }}
                </small>
              </div>
            </td>
            <td>
              <v-switch
                :input-value="props.item.is_active"
                :disabled="!canManagePostingRules"
                hide-details
                @change="toggleRule(props.item, $event)"
              />
            </td>
            <td>
              <v-btn v-if="canManagePostingRules" small flat color="primary" @click="openEditDialog(props.item)">
                {{ $t("finance.postingRules.editMapping") }}
              </v-btn>
              <v-btn
                v-if="props.item.admin_triggerable"
                small
                flat
                color="teal"
                @click="openTriggerDialog(props.item)"
              >
                {{ $t("finance.postingRules.manualEntry") }}
              </v-btn>
            </td>
          </template>
        </v-data-table>
      </v-card-text>

      <!-- Failures -->
      <v-card-text v-show="tab === 1">
        <v-data-table class="fin-dt" :headers="failureHeaders" :items="failures" hide-actions item-key="id">
          <template v-slot:items="props">
            <td>{{ props.item.id }}</td>
            <td>{{ props.item.event_key }}</td>
            <td>{{ props.item.operation_ref || '-' }}</td>
            <td><span class="fin-ellipsis" style="--fin-name-max: 380px" :title="props.item.error">{{ props.item.error }}</span></td>
            <td>{{ formatDate(props.item.created_at) }}</td>
            <td>
              <v-chip v-if="props.item.journal_entry_id" small color="success" text-color="white">
                {{ $t("finance.postingRules.retried") }}
              </v-chip>
              <!-- Operational entries doc 2026-10-03 (items 1.3/1.4): a
                   review-only record has nothing to retry. -->
              <v-tooltip v-else-if="isReviewOnlyFailure(props.item)" top>
                <v-chip slot="activator" small color="orange" text-color="white">
                  {{ $t("finance.postingRules.reviewOnly") }}
                </v-chip>
                <span>{{ $t("finance.postingRules.reviewOnlyHint") }}</span>
              </v-tooltip>
              <v-btn v-else small flat color="primary" @click="retryFailure(props.item)">
                {{ $t("finance.postingRules.retry") }}
              </v-btn>
            </td>
          </template>
        </v-data-table>
      </v-card-text>

      <!-- Depreciation schedules -->
      <v-card-text v-show="tab === 2">
        <div class="fin-filters">
          <v-text-field
            v-model.number="depreciationForm.car_id"
            :label="$t('finance.postingRules.carId')"
            type="number"
            hide-details
            class="fin-field"
          />
          <v-text-field
            v-model.number="depreciationForm.monthly_amount"
            :label="$t('finance.postingRules.monthlyAmount')"
            type="number"
            hide-details
            class="fin-field"
          />
          <v-text-field
            v-model="depreciationForm.start_month"
            :label="$t('finance.postingRules.startMonth')"
            placeholder="2026-08"
            hide-details
            class="fin-field"
          />
          <v-text-field
            v-model="depreciationForm.notes"
            :label="$t('finance.postingRules.notes')"
            hide-details
            class="fin-field--wide"
          />
          <v-btn small color="primary" @click="addSchedule">{{ $t("finance.postingRules.addSchedule") }}</v-btn>
        </div>

        <v-data-table class="fin-dt" :headers="scheduleHeaders" :items="schedules" hide-actions item-key="id">
          <template v-slot:items="props">
            <td>{{ props.item.car ? props.item.car.plate_number : props.item.car_id }}</td>
            <td class="fin-num">{{ props.item.monthly_amount }}</td>
            <td>{{ (props.item.start_month || '').substring(0, 7) }}</td>
            <td>{{ props.item.end_month ? props.item.end_month.substring(0, 7) : '-' }}</td>
            <td>
              <v-switch
                :input-value="props.item.is_active"
                hide-details
                @change="toggleSchedule(props.item, $event)"
              />
            </td>
          </template>
        </v-data-table>

        <v-layout wrap align-center class="mt-3">
          <v-flex xs12 md3 class="pa-2">
            <v-text-field v-model="runMonth" :label="$t('finance.postingRules.runMonth')" placeholder="2026-07" />
          </v-flex>
          <v-flex xs12 md3 class="pa-2">
            <v-btn color="teal" dark :disabled="!runMonth" @click="runDepreciation">
              {{ $t("finance.postingRules.runDepreciation") }}
            </v-btn>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { formatFinanceDate } from "@/mixins/financeDate";
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";

// A resolver line resolves its GL account from an entity id (or a
// payment-method code for gateways). Maps the line's resolver_key to the
// context input storeOperationEntry accepts for it.
const RESOLVER_CONTEXT_FIELDS = {
  captain_deferred: { key: "captain_id", label: "captainId", type: "number" },
  captain_deferred_counterparty: { key: "captain_id", label: "captainId", type: "number" },
  company_captain_deferred: { key: "captain_id", label: "captainId", type: "number" },
  owner_payable: { key: "car_owner_id", label: "carOwnerId", type: "number" },
  business_deferred: { key: "business_account_id", label: "businessAccountId", type: "number" },
  center_deferred: { key: "transfer_center_id", label: "transferCenterId", type: "number" },
  center_receivable: { key: "transfer_center_id", label: "transferCenterId", type: "number" },
  gateway_asset: { key: "payment_method_code", label: "paymentMethodCode", type: "text" },
};

// The engine drops a line whose amount rounds to (near) zero before it ever
// resolves that line's account (AutoPostingService::postOrFail).
const POSTED_AMOUNT_EPSILON = 0.005;

export default {
  name: "PostingRules",
  components: { FinanceSectionNav, LoadingComponent },
  data() {
    return {
      loading: false,
      tab: 0,
      engineEnabled: false,
      rules: [],
      accounts: [],
      failures: [],
      schedules: [],
      editDialog: false,
      editedRule: null,
      editForm: { lines: [] },
      triggerDialog: false,
      triggeredRule: null,
      triggerForm: { amounts: {}, operation_ref: "", journal_date: null, memo: "" },
      ruleSearch: "",
      ruleKind: "all",
      depreciationForm: { car_id: null, monthly_amount: null, start_month: "", notes: "" },
      runMonth: "",
    };
  },
  computed: {
    canManagePostingRules() {
      // Accounting review 2026-09-01: rule editing (PUT posting-rules/{rule})
      // moved behind MANAGE_POSTING_RULES on the backend (Super Admin bypasses
      // via hasPermission). Viewing the page and posting manual entries stay
      // under the page permission (MANAGE_FINANCE_ACCOUNTS).
      return this.hasPermission("MANAGE_POSTING_RULES");
    },
    ruleHeaders() {
      return [
        { text: this.$t("finance.postingRules.docRef"), value: "doc_ref", sortable: false },
        { text: this.$t("finance.postingRules.eventKey"), value: "event_key", sortable: false },
        { text: this.$t("finance.postingRules.ruleName"), value: "name", sortable: false },
        { text: this.$t("finance.postingRules.mapping"), value: "lines", sortable: false },
        { text: this.$t("finance.postingRules.active"), value: "is_active", sortable: false },
        { text: "", value: "actions", sortable: false },
      ];
    },
    failureHeaders() {
      return [
        { text: "#", value: "id", sortable: false },
        { text: this.$t("finance.postingRules.eventKey"), value: "event_key", sortable: false },
        { text: this.$t("finance.postingRules.operationRef"), value: "operation_ref", sortable: false },
        { text: this.$t("finance.postingRules.error"), value: "error", sortable: false },
        { text: this.$t("finance.common.date"), value: "created_at", sortable: false },
        { text: "", value: "actions", sortable: false },
      ];
    },
    scheduleHeaders() {
      return [
        { text: this.$t("finance.postingRules.car"), value: "car_id", sortable: false },
        { text: this.$t("finance.postingRules.monthlyAmount"), value: "monthly_amount", sortable: false, align: "end" },
        { text: this.$t("finance.postingRules.startMonth"), value: "start_month", sortable: false },
        { text: this.$t("finance.postingRules.endMonth"), value: "end_month", sortable: false },
        { text: this.$t("finance.postingRules.active"), value: "is_active", sortable: false },
      ];
    },
    accountOptions() {
      // Policy doc 2026-08-23 item 1: overrides may only target active LEAF
      // accounts — pointing an engine line at a parent (aggregate) row is the
      // same silent slippage the manual screens refuse. Mirrors the backend
      // updateRule guard.
      return (this.accounts || [])
        .filter(function (account) {
          return (
            account.is_active !== false &&
            account.has_children !== true &&
            account.kind !== "closing"
          );
        })
        .map(
          function (account) {
            return {
              text: account.code + " - " + this.accountName(account),
              value: account.id,
            };
          }.bind(this)
        );
    },
    ruleKindOptions() {
      return [
        { value: "all", text: this.$t("finance.postingRules.kindAll") },
        { value: "manual", text: this.$t("finance.postingRules.kindManual") },
        { value: "automatic", text: this.$t("finance.postingRules.kindAutomatic") },
      ];
    },
    filteredRules() {
      // The Go-Live manual (2026-09-07) roughly doubled the rule list, so the
      // screen filters. "Manual" = the document's M entries, which are also
      // exactly the rules the accountant can post herself; "automatic" = the
      // A entries, fired by an operational flow. A rule with no document
      // reference falls back to its admin_triggerable flag.
      var needle = (this.ruleSearch || "").trim().toLowerCase();
      var kind = this.ruleKind;

      return (this.rules || []).filter(
        function (rule) {
          if (kind !== "all" && this.ruleKindOf(rule) !== kind) {
            return false;
          }
          if (!needle) {
            return true;
          }
          return [rule.event_key, rule.name_ar, rule.name_en, rule.doc_ref, rule.applies_when, rule.applies_when_en]
            .filter(Boolean)
            .some(function (field) {
              return String(field).toLowerCase().indexOf(needle) !== -1;
            });
        }.bind(this)
      );
    },
    triggeredRuleNeedsCar() {
      return !!this.triggeredRule && ["car_disposal_sale", "car_disposal_scrap"].indexOf(this.triggeredRule.event_key) !== -1;
    },
    triggerAmountKeys() {
      if (!this.triggeredRule) {
        return [];
      }
      var keys = [];
      (this.triggeredRule.lines || []).forEach(function (line) {
        if (keys.indexOf(line.amount_key) === -1) {
          keys.push(line.amount_key);
        }
      });
      return keys;
    },
    triggerContextFields() {
      // Inputs the triggered rule can need, derived from its resolver lines,
      // so storeOperationEntry can resolve them; the server validates the
      // entity exists. Always OFFERED when the rule has such a line — see
      // requiredTriggerContextKeys for the far narrower set that is required.
      if (!this.triggeredRule) {
        return [];
      }
      var fields = [];
      (this.triggeredRule.lines || []).forEach(function (line) {
        var field = line.resolver_key ? RESOLVER_CONTEXT_FIELDS[line.resolver_key] : null;
        if (
          field &&
          !fields.some(function (existing) {
            return existing.key === field.key;
          })
        ) {
          fields.push(field);
        }
      });
      return fields;
    },
    requiredTriggerContextKeys() {
      // A resolver line only consumes context when the engine actually
      // resolves it: AutoPostingService::resolveLineAccount returns the
      // per-line gl_account_id override FIRST, and postOrFail skips near-zero
      // lines before resolving their account at all. Demanding an id in those
      // two cases locked the accountant out of entries she could post before,
      // and any filler captain id she typed to get past it would stamp
      // journal_entries.captain_id with a driver who has no line in the entry
      // (accounting review 2026-09-01).
      if (!this.triggeredRule) {
        return [];
      }
      var keys = [];
      (this.triggeredRule.lines || []).forEach(
        function (line) {
          var field = line.resolver_key ? RESOLVER_CONTEXT_FIELDS[line.resolver_key] : null;
          if (!field || line.gl_account_id) {
            return;
          }
          var amount = Number(this.triggerForm.amounts[line.amount_key]);
          if (!(Math.abs(amount) >= POSTED_AMOUNT_EPSILON)) {
            // Blank, zero and non-numeric amounts all land here (NaN
            // comparisons are false), matching the dropped-line behavior.
            return;
          }
          if (keys.indexOf(field.key) === -1) {
            keys.push(field.key);
          }
        }.bind(this)
      );
      return keys;
    },
    hasAmountToPost() {
      return Object.keys(this.filledAmounts).length > 0;
    },
    filledAmounts() {
      // Blank / non-numeric legs are dropped rather than sent as "" (the
      // backend validates every amount as numeric). The engine drops a
      // zero leg anyway, so an explicit 0 is harmless.
      var filled = {};
      var amounts = this.triggerForm.amounts || {};
      Object.keys(amounts).forEach(function (key) {
        var value = amounts[key];
        if (value === null || value === undefined || value === "") {
          return;
        }
        var numeric = Number(value);
        if (!isNaN(numeric)) {
          filled[key] = numeric;
        }
      });
      return filled;
    },
    triggerContextMissing() {
      return this.requiredTriggerContextKeys.some(
        function (key) {
          var value = this.triggerForm[key];
          return value === null || value === undefined || value === "";
        }.bind(this)
      );
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    loadAll() {
      this.loadRules();
      this.loadAccounts();
      this.loadFailures();
      this.loadSchedules();
    },
    loadRules() {
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_POSTING_RULES)
        .then(
          function (response) {
            var data = response.data.data || {};
            this.rules = data.rules || [];
            this.engineEnabled = !!data.auto_posting_enabled;
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit("SHOW_TOAST", this.getErrorMessage(error, this.$t("finance.postingRules.failedLoad")), "red");
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    loadAccounts() {
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_CHART_OF_ACCOUNTS)
        .then(
          function (response) {
            this.accounts = response.data.data || [];
          }.bind(this)
        )
        .catch(function () {});
    },
    loadFailures() {
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_POSTING_FAILURES, { params: { perPage: 100 } })
        .then(
          function (response) {
            this.failures = (response.data.data && response.data.data.data) || [];
          }.bind(this)
        )
        .catch(function () {});
    },
    loadSchedules() {
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_DEPRECIATION_SCHEDULES)
        .then(
          function (response) {
            this.schedules = response.data.data || [];
          }.bind(this)
        )
        .catch(function () {});
    },
    appliesWhen(rule) {
      if (!rule) return "";
      return this.$i18n.locale === "ar" ? rule.applies_when || rule.applies_when_en || "" : rule.applies_when_en || rule.applies_when || "";
    },
    ruleLabel(rule) {
      return this.$i18n.locale === "ar" ? rule.name_ar || rule.name_en : rule.name_en || rule.name_ar;
    },
    accountName(account) {
      return this.$i18n.locale === "ar" ? account.name_ar || account.name : account.name || account.name_ar;
    },
    ruleKindOf(rule) {
      if (rule.doc_ref) {
        return String(rule.doc_ref).charAt(0) === "M" ? "manual" : "automatic";
      }
      return rule.admin_triggerable ? "manual" : "automatic";
    },
    docRefColor(docRef) {
      return String(docRef).charAt(0) === "M" ? "indigo" : "teal";
    },
    formatAmountKey(key) {
      var known = {
        amount: this.$t("finance.common.amount"),
        total: this.$t("finance.postingRules.amountTotal"),
        gateway_paid: this.$t("finance.postingRules.amountGateway"),
        wallet_paid: this.$t("finance.postingRules.amountWallet"),
        business_paid: this.$t("finance.postingRules.amountBusiness"),
        captain_net_debit: this.$t("finance.postingRules.amountCaptainNet"),
        captain_cash_collected: this.$t("finance.postingRules.amountCaptainCashCollected"),
        discount: this.$t("finance.postingRules.amountDiscount"),
        company_commission: this.$t("finance.postingRules.amountCommission"),
        company_commission_third_party: this.$t("finance.postingRules.amountCommissionThirdParty"),
        ride_revenue: this.$t("finance.postingRules.amountRideRevenue"),
        driver_share: this.$t("finance.postingRules.amountDriverShare"),
        owner_share: this.$t("finance.postingRules.amountOwnerShare"),
        store_commission: this.$t("finance.postingRules.amountStoreCommission"),
        delivery_fee: this.$t("finance.postingRules.amountDeliveryFee"),
        driver_due: this.$t("finance.postingRules.amountDriverDue"),
        store_due: this.$t("finance.postingRules.amountStoreDue"),
        // Go-Live manual (2026-09-07). The money-channel keys are the
        // "cash OR bank OR gateway" alternatives of the document: fill the
        // one you used, the others drop out of the entry.
        cash: this.$t("finance.postingRules.amountCash"),
        bank: this.$t("finance.postingRules.amountBank"),
        gateway: this.$t("finance.postingRules.amountGatewayChannel"),
        net: this.$t("finance.postingRules.amountNet"),
        gross: this.$t("finance.postingRules.amountGross"),
        fee: this.$t("finance.postingRules.amountFee"),
        gateway_net: this.$t("finance.postingRules.amountGatewayNet"),
        captain_share: this.$t("finance.postingRules.amountCaptainShare"),
        platform_commission: this.$t("finance.postingRules.amountPlatformCommission"),
        credit_consumed: this.$t("finance.postingRules.amountCreditConsumed"),
        captain_favour: this.$t("finance.postingRules.amountCaptainFavour"),
        center_favour: this.$t("finance.postingRules.amountCenterFavour"),
        company_loss: this.$t("finance.postingRules.amountCompanyLoss"),
        company_gain: this.$t("finance.postingRules.amountCompanyGain"),
      };
      return known[key] || key;
    },
    defaultAccountLabel(line) {
      let base;
      if (line.account_mode === "resolver") {
        base = this.$t("finance.postingRules.resolver_" + line.resolver_key) !== "finance.postingRules.resolver_" + line.resolver_key
          ? this.$t("finance.postingRules.resolver_" + line.resolver_key)
          : line.resolver_key;
      } else {
        base = line.system_key || "-";
      }
      // QA V3 finance-1: prefix the resolved numeric GL code so accountants
      // can verify the mapping digitally (resolver rows show the sub-ledger
      // pattern, e.g. 2201NNNNNN). resolved_account comes from the backend.
      const resolved = line.resolved_account;
      if (resolved && resolved.missing) {
        return "⚠ " + base + " (" + this.$t("finance.postingRules.accountMissing") + ")";
      }
      // Doc 2026-10-03 item 1.6: resolver lines carry the REAL account
      // pattern (e.g. 2132101 + governorate + driver id, 2118 + owner id).
      const pattern = resolved
        ? (this.$i18n.locale === "ar" ? resolved.account_pattern || resolved.account_pattern_en : resolved.account_pattern_en || resolved.account_pattern)
        : null;
      if (pattern) {
        return pattern + " · " + base;
      }
      if (resolved && (resolved.code_pattern || resolved.code)) {
        return (resolved.code_pattern || resolved.code) + " · " + base;
      }
      return base;
    },
    openEditDialog(rule) {
      this.editedRule = rule;
      this.editForm = {
        lines: (rule.lines || []).map(function (line) {
          return {
            id: line.id,
            entry_type: line.entry_type,
            amount_key: line.amount_key,
            account_mode: line.account_mode,
            system_key: line.system_key,
            resolver_key: line.resolver_key,
            gl_account_id: line.gl_account_id,
          };
        }),
      };
      this.editDialog = true;
    },
    closeEditDialog() {
      this.editDialog = false;
      this.editedRule = null;
    },
    saveRule() {
      this.loading = true;
      this.axios
        .put(this.api.ACTIONS.ACCOUNTING_POSTING_RULE(this.editedRule.id), {
          lines: this.editForm.lines.map(function (line) {
            return { id: line.id, gl_account_id: line.gl_account_id || null };
          }),
        })
        .then(
          function () {
            this.closeEditDialog();
            this.loadRules();
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.postingRules.saved"), "green");
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit("SHOW_TOAST", this.getErrorMessage(error, this.$t("finance.postingRules.failedSave")), "red");
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    toggleRule(rule, value) {
      this.axios
        .put(this.api.ACTIONS.ACCOUNTING_POSTING_RULE(rule.id), { is_active: !!value })
        .then(
          function () {
            this.loadRules();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit("SHOW_TOAST", this.getErrorMessage(error, this.$t("finance.postingRules.failedSave")), "red");
            this.loadRules();
          }.bind(this)
        );
    },
    openTriggerDialog(rule) {
      this.triggeredRule = rule;
      // Declared up front: a property added to `amounts` later by v-model is
      // invisible to Vue 2 reactivity, so the required-context check would
      // not re-run when the accountant types an amount.
      var amounts = {};
      (rule.lines || []).forEach(function (line) {
        amounts[line.amount_key] = null;
      });
      this.triggerForm = {
        amounts: amounts,
        operation_ref: "",
        journal_date: null,
        memo: "",
        car_id: null,
        captain_id: null,
        car_owner_id: null,
        business_account_id: null,
        transfer_center_id: null,
        payment_method_code: "",
      };
      this.triggerDialog = true;
    },
    submitTrigger() {
      this.loading = true;
      this.axios
        .post(this.api.ACTIONS.ACCOUNTING_OPERATION_ENTRIES, {
          event_key: this.triggeredRule.event_key,
          amounts: this.filledAmounts,
          operation_ref: this.triggerForm.operation_ref,
          journal_date: this.triggerForm.journal_date || undefined,
          memo: this.triggerForm.memo || undefined,
          car_id: this.triggerForm.car_id || undefined,
          // Resolver context ids: unused ones stay null/empty and are dropped.
          captain_id: this.triggerForm.captain_id || undefined,
          car_owner_id: this.triggerForm.car_owner_id || undefined,
          business_account_id: this.triggerForm.business_account_id || undefined,
          transfer_center_id: this.triggerForm.transfer_center_id || undefined,
          payment_method_code: this.triggerForm.payment_method_code || undefined,
        })
        .then(
          function () {
            this.triggerDialog = false;
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.postingRules.posted"), "green");
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit("SHOW_TOAST", this.getErrorMessage(error, this.$t("finance.postingRules.failedPost")), "red");
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    isReviewOnlyFailure(failure) {
      return ['gateway_payment_after_settlement'].indexOf(failure.event_key) !== -1;
    },
    retryFailure(failure) {
      this.loading = true;
      this.axios
        .post(this.api.ACTIONS.ACCOUNTING_POSTING_FAILURE_RETRY(failure.id))
        .then(
          function () {
            this.loadFailures();
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.postingRules.posted"), "green");
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit("SHOW_TOAST", this.getErrorMessage(error, this.$t("finance.postingRules.failedPost")), "red");
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    addSchedule() {
      this.loading = true;
      this.axios
        .post(this.api.ACTIONS.ACCOUNTING_DEPRECIATION_SCHEDULES, this.depreciationForm)
        .then(
          function () {
            this.depreciationForm = { car_id: null, monthly_amount: null, start_month: "", notes: "" };
            this.loadSchedules();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit("SHOW_TOAST", this.getErrorMessage(error, this.$t("finance.postingRules.failedSave")), "red");
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    toggleSchedule(schedule, value) {
      this.axios
        .put(this.api.ACTIONS.ACCOUNTING_DEPRECIATION_SCHEDULE(schedule.id), { is_active: !!value })
        .then(
          function () {
            this.loadSchedules();
          }.bind(this)
        )
        .catch(function () {});
    },
    runDepreciation() {
      this.loading = true;
      this.axios
        .post(this.api.ACTIONS.ACCOUNTING_DEPRECIATION_RUNS, { month: this.runMonth })
        .then(
          function (response) {
            var output = (response.data.data && response.data.data.output) || "";
            window.getApp.$emit("SHOW_TOAST", output || this.$t("finance.postingRules.posted"), "green");
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit("SHOW_TOAST", this.getErrorMessage(error, this.$t("finance.postingRules.failedPost")), "red");
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    formatDate(value) {
      // One shared finance formatter (accountant doc 2026-10-03 item 1.8).
      return formatFinanceDate(value);
    },
    getErrorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return fallback;
    },
  },
};
</script>
